/**
 * Component Lifecycle for JelenJS
 */

import { createEffect } from '../reactive';

// Global state key for component lifecycle
const GLOBAL_COMPONENT_STATE_KEY = '__JELENJS_COMPONENT_STATE__';

// Initialize global component state
function getGlobalComponentState() {
  if (typeof globalThis !== 'undefined') {
    if (!(globalThis as any)[GLOBAL_COMPONENT_STATE_KEY]) {
      (globalThis as any)[GLOBAL_COMPONENT_STATE_KEY] = {
        currentComponent: null,
        mountCallbacks: new WeakMap<any, (() => void)[]>(),
        unmountCallbacks: new WeakMap<any, (() => void)[]>()
      };
    }
    return (globalThis as any)[GLOBAL_COMPONENT_STATE_KEY];
  }
  
  if (typeof window !== 'undefined') {
    if (!(window as any)[GLOBAL_COMPONENT_STATE_KEY]) {
      (window as any)[GLOBAL_COMPONENT_STATE_KEY] = {
        currentComponent: null,
        mountCallbacks: new WeakMap<any, (() => void)[]>(),
        unmountCallbacks: new WeakMap<any, (() => void)[]>()
      };
    }
    return (window as any)[GLOBAL_COMPONENT_STATE_KEY];
  }
  
  // Fallback for non-browser environments
  return {
    currentComponent: null,
    mountCallbacks: new WeakMap<any, (() => void)[]>(),
    unmountCallbacks: new WeakMap<any, (() => void)[]>()
  };
}

const globalComponentState = getGlobalComponentState();

/**
 * Set the current component context (used internally)
 */
export function setCurrentComponent(component: any): void {
    console.log('[jelenjs-lifecycle] setCurrentComponent called, component:', component);
    globalComponentState.currentComponent = component;
}

/**
 * Get the current component context
 */
export function getCurrentComponent(): any {
    return globalComponentState.currentComponent;
}

/**
 * Register a callback to be executed when the component is mounted to the DOM
 * @param callback Function to execute after mounting
 */
export function onMount(callback: () => void): void {
    console.log('[jelenjs-lifecycle] onMount called, currentComponent:', globalComponentState.currentComponent);
    if (typeof callback !== 'function') return;
    
    // If we have a current component context, register the callback
    if (globalComponentState.currentComponent) {
        if (!globalComponentState.mountCallbacks.has(globalComponentState.currentComponent)) {
            globalComponentState.mountCallbacks.set(globalComponentState.currentComponent, []);
        }
        globalComponentState.mountCallbacks.get(globalComponentState.currentComponent)!.push(callback);
    } else {
        // If no component context, execute immediately (fallback behavior)
        // This ensures compatibility with existing code
        setTimeout(callback, 0);
    }
}

/**
 * Register a callback to be executed when the component is unmounted from the DOM
 * @param callback Function to execute before unmounting
 */
export function onUnmount(callback: () => void): void {
    console.log('[jelenjs-lifecycle] onUnmount called, currentComponent:', globalComponentState.currentComponent);
    if (typeof callback !== 'function') return;
    
    if (globalComponentState.currentComponent) {
        if (!globalComponentState.unmountCallbacks.has(globalComponentState.currentComponent)) {
            globalComponentState.unmountCallbacks.set(globalComponentState.currentComponent, []);
        }
        globalComponentState.unmountCallbacks.get(globalComponentState.currentComponent)!.push(callback);
    }
}

/**
 * Register a callback to be executed when the component updates
 * @param callback Function to execute on update
 */
export function onUpdate(callback: () => void): void {
    // For updates, we can use createEffect as it tracks dependencies
    if (typeof callback === 'function') {
        createEffect(() => {
            callback();
        });
    }
}

/**
 * Similar to React's useEffect, combines onMount, onUpdate and onUnmount
 * @param callback Effect function that can return a cleanup function
 * @param deps Optional dependency array for selective updates
 */
export function useEffect(callback: () => (() => void) | void, deps?: any[]): void {
    if (typeof callback === 'function') {
        createEffect(() => {
            const cleanup = callback();
            
            // If callback returns a cleanup function, register it for unmount
            if (typeof cleanup === 'function') {
                onUnmount(cleanup);
            }
        });
    }
}

/**
 * Get the current element being rendered
 */
export function getCurrentElement(): HTMLElement | null {
    return null;
}

/**
 * Set the current element being rendered (for internal use)
 */
export function setCurrentElement(element: HTMLElement | null): void {
    // Simplified implementation, no-op
}

/**
 * Mark a component as mounted and run mount callbacks
 * @param component The component instance
 */
export function markAsMounted(component: any): void {
    console.log('[jelenjs-lifecycle] markAsMounted called, component:', component);
    const callbacks = globalComponentState.mountCallbacks.get(component);
    if (callbacks) {
        callbacks.forEach((callback: () => void) => {
            try {
                callback();
            } catch (error) {
                console.error('Error in onMount callback:', error);
            }
        });
        // Clear callbacks after execution
        globalComponentState.mountCallbacks.delete(component);
    }
}

/**
 * Handle component unmount and run cleanup
 * @param component The component instance
 */
export function handleUnmount(component: any): void {
    const callbacks = globalComponentState.unmountCallbacks.get(component);
    if (callbacks) {
        callbacks.forEach((callback: () => void) => {
            try {
                callback();
            } catch (error) {
                console.error('Error in onUnmount callback:', error);
            }
        });
        // Clear callbacks after execution
        globalComponentState.unmountCallbacks.delete(component);
    }
} 